import { memo } from "react";

import { BreadcrumbsPlaceholder } from "@components/Breadcrumbs";
import { CollectionAdjacentCollectionsPlaceholder } from "@react/sections/Collection/CollectionFilterNavigation";
import { CollectionGridPlaceholder } from "@react/sections/Collection/CollectionGrid";
import { CollectionHeroImagePlaceholder } from "@react/sections/Collection/CollectionHero";
import { tw, type TTailwindString } from "classnames";

const styles = {
  base: tw("animate-fade-in", "duration-1000"),
};

const heroStyles = {
  base: tw(
    "mx-auto",
    "max-w-screen-xl",
    "mg:flex",
    "sm:pt-4",
    "mg:pt-12",
    "mg:px-12"
  ),
  content: (hasHeroImage: boolean) =>
    tw(
      "flex",
      "flex-col",
      "mg:w-full",
      "pt-4",
      "sm:pt-6",
      "p-6",
      "mg:p-0",
      "mg:pr-10",
      "gap-y-6",
      {
        "pb-4": !hasHeroImage,
        "pb-6": hasHeroImage,
      }
    ),
  textContainer: tw("flex", "flex-col", "justify-center", "h-full"),
  title: tw(
    "mb-3",
    "md:mb-4",
    "h-8",
    "md:h-12",
    "w-1/2",
    "rounded-full",
    "bg-placeholder",
    "animate-pulse"
  ),
  description1: tw(
    "h-4",
    "md:h-6",
    "w-full",
    "rounded-full",
    "bg-placeholder",
    "animate-pulse",
    "mb-2"
  ),
  description2: (hasHeroImage: boolean) =>
    tw(
      "h-4",
      "md:h-6",
      "w-1/2",
      "rounded-full",
      "bg-placeholder",
      "animate-pulse",
      "md:hidden",
      "mg:block",
      {
        "mg:hidden": !hasHeroImage,
      }
    ),
  heroImageContainer: tw(
    "w-full",
    "mg:w-3/5",
    "shrink-0",
    "aspect-[1024/320]" as TTailwindString,
    "mg:aspect-[812/300]" as TTailwindString
  ),
};

const filterStyles = {
  base: tw(
    "sticky",
    "top-[56px]" as TTailwindString,
    "mg:top-[65px]" as TTailwindString,
    "z-10",
    "mt-4",
    "border-0",
    "border-b",
    "border-solid",
    "border-divider",
    "bg-background-primary"
  ),
  inner: tw(
    "flex",
    "items-center",
    "mx-auto",
    "max-w-screen-xl",
    "px-6",
    "py-4",
    "mg:px-12",
    "mg:py-[21.5px]" as TTailwindString
  ),
  filterButton: tw(
    "h-8",
    "w-[114px]" as TTailwindString,
    "rounded",
    "bg-placeholder",
    "animate-pulse",
    "shrink-0",
    "border-2",
    "border-solid",
    "border-transparent"
  ),
  adjacentButtons: tw(
    "pl-4",
    "pr-6",
    "ml-4",
    "-mr-6",
    "mg:-mr-12",
    "mg:pr-12",
    "lg:pl-6",
    "lg:ml-6",
    "border-0",
    "border-l",
    "border-solid",
    "border-divider",
    "overflow-x-auto"
  ),
};

const mainStyles = {
  base: tw("mx-auto", "flex", "max-w-screen-xl", "px-6", "mg:px-12"),
  sidebar: tw(
    "sticky",
    "hidden",
    "w-collections-sidebar",
    "self-start",
    "pr-7",
    "pt-7.5",
    "mg:top-[140px]" as TTailwindString,
    "mg:block",
    "h-[calc(100svh-140px)]" as TTailwindString,
    "overflow-y-auto",
    "pl-1",
    "-ml-1"
  ),
  sort: tw(
    "ml-auto",
    "h-8",
    "w-36",
    "md:w-40",
    "rounded",
    "bg-placeholder",
    "animate-pulse"
  ),
  loadMoreContainer: tw(
    "box-border",
    "translate-y-0",
    "overflow-hidden",
    "px-6",
    "pb-20",
    "pt-16",
    "text-center",
    "opacity-100",
    "transition-all",
    "duration-500",
    "mg:px-12",
    "mg:pt-20"
  ),
  loadMoreButton: tw(
    "w-[184px]" as TTailwindString,
    "h-12",
    "rounded",
    "bg-placeholder",
    "animate-pulse"
  ),
};

const CollectionPlaceholderBase = ({
  showHeroImage,
}: {
  showHeroImage: boolean;
}) => {
  return (
    <div className={styles.base}>
      <div className={heroStyles.base}>
        <div className={heroStyles.content(showHeroImage)}>
          <BreadcrumbsPlaceholder />
          <div className={heroStyles.textContainer}>
            <div className={heroStyles.title}></div>
            <div className={heroStyles.description1}></div>
            <div className={heroStyles.description2(showHeroImage)}></div>
          </div>
        </div>
        {showHeroImage && (
          <div className={heroStyles.heroImageContainer}>
            <CollectionHeroImagePlaceholder />
          </div>
        )}
      </div>
      <div className={filterStyles.base}>
        <div className={filterStyles.inner}>
          <div className={filterStyles.filterButton}></div>
          <div className={filterStyles.adjacentButtons}>
            <CollectionAdjacentCollectionsPlaceholder />
          </div>
        </div>
      </div>
      <div className={mainStyles.base}>
        <div className="w-full scroll-mt-[140px]">
          <div className="mb-2 flex py-3.5 mg:py-[22px]">
            <div className={mainStyles.sort}></div>
          </div>
          <CollectionGridPlaceholder />
          <div className={mainStyles.loadMoreContainer}>
            <div className={mainStyles.loadMoreButton}></div>
          </div>
        </div>
      </div>
    </div>
  );
};

export const CollectionPlaceholder = memo(CollectionPlaceholderBase);
